import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  DocumentData,
  QueryConstraint
} from 'firebase/firestore';
import { db } from '@lib/firebase';

import { COURSE_IDS } from '@/shared/constants';
import { ValidationError, SchedulingError } from '@/shared/api/errors/ApiError';
import { getFirestoreTimestamps } from '@/shared/utils/api/timestampHelper';
import ServiceBase from '@/shared/api/base/ServiceBase';

interface TimeSlotData {
  instructor: string;
  date: string;
  startTime: string;
  endTime: string;
  capacity?: number;
  location?: string;
  [key: string]: any;
}

interface FilterOptions {
  date?: string;
  isAvailable?: boolean;
}

interface Booking {
  userId: string;
  userEmail: string;
  bookedAt: string;
}

interface TimeSlot {
  id: string;
  instructor: string;
  date: string;
  startTime: string;
  endTime: string;
  capacity?: number;
  location?: string;
  isAvailable: boolean;
  bookedBy: Booking[];
  assignedTo?: string | null;
  assignedToEmail?: string | null;
  assignedAt?: any;
  createdAt?: any;
  updatedAt?: any;
}

class SchedulingService extends ServiceBase {
  constructor() {
    super('SchedulingService');
  }

  async createTimeSlot(timeSlotData: TimeSlotData): Promise<TimeSlot> {
    try {
      this.validate.validateTimeSlotData(timeSlotData);

      const slotRef = doc(collection(db, 'timeSlots'));

      const slot = {
        ...timeSlotData,
        isAvailable: true,
        bookedBy: [],
        ...getFirestoreTimestamps()
      };

      await setDoc(slotRef, slot);
      this.log(`Created time slot ${slotRef.id} for instructor ${timeSlotData.instructor}`);

      return {
        id: slotRef.id,
        ...slot
      } as TimeSlot;
    } catch (error) {
      this.logError(error, { method: 'createTimeSlot' });
      throw error;
    }
  }

  async getTimeSlots(filters: FilterOptions = {}): Promise<TimeSlot[]> {
    try {
      if (typeof filters !== 'object' || filters === null) {
        throw new ValidationError('Filters must be an object');
      }

      const slotsRef = collection(db, 'timeSlots');

      let q: any = slotsRef;
      if (filters.date) {
        q = query(slotsRef, where('date', '==', filters.date));
      }
      if (filters.isAvailable !== undefined) {
        q = query(slotsRef, where('isAvailable', '==', filters.isAvailable));
      }

      const querySnapshot = await getDocs(q);
      const slots: TimeSlot[] = [];

      querySnapshot.forEach(docSnap => {
        const data = docSnap.data();
        slots.push({
          id: docSnap.id,
          ...data,
          bookedBy: data.bookedBy || []
        } as TimeSlot);
      });

      return slots.sort((a, b) => {
        const timeA = new Date(`${a.date} ${a.startTime}`).getTime();
        const timeB = new Date(`${b.date} ${b.startTime}`).getTime();
        return timeA - timeB;
      });
    } catch (error) {
      this.logError(error, { method: 'getTimeSlots' });
      throw error;
    }
  }

  async getAvailableTimeSlots(startDate: Date, endDate: Date): Promise<TimeSlot[]> {
    try {
      if (!(startDate instanceof Date)) {
        throw new ValidationError('Start date must be a Date object');
      }
      if (!(endDate instanceof Date)) {
        throw new ValidationError('End date must be a Date object');
      }

      const slotsRef = collection(db, 'timeSlots');
      const q = query(
        slotsRef,
        where('isAvailable', '==', true)
      );

      const querySnapshot = await getDocs(q);
      const slots: TimeSlot[] = [];

      querySnapshot.forEach(docSnap => {
        const slotData = docSnap.data() as DocumentData;
        const slotDate = new Date(slotData.date);

        if (slotDate >= startDate && slotDate <= endDate) {
          slots.push({
            id: docSnap.id,
            ...slotData
          } as TimeSlot);
        }
      });

      return slots.sort((a, b) => {
        const dateA = new Date(`${a.date} ${a.startTime}`).getTime();
        const dateB = new Date(`${b.date} ${b.startTime}`).getTime();
        return dateA - dateB;
      });
    } catch (error) {
      this.logError(error, { method: 'getAvailableTimeSlots' });
      throw error;
    }
  }

  async bookTimeSlot(userId: string, slotId: string, userEmail: string): Promise<{ success: boolean; booking: Booking; slot: DocumentData }> {
    try {
      this.validate.validateUserId(userId);
      if (!slotId || typeof slotId !== 'string') {
        throw new ValidationError('Slot ID is required');
      }
      if (!userEmail || typeof userEmail !== 'string' || !userEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
        throw new ValidationError('User email must be valid format');
      }

      const slotRef = doc(db, 'timeSlots', slotId);
      const slotDoc = await getDoc(slotRef);

      if (!slotDoc.exists()) {
        throw new SchedulingError('Time slot not found');
      }

      const slotData = slotDoc.data();
      const bookedBy: Booking[] = slotData.bookedBy || [];

      if (bookedBy.some(booking => booking.userId === userId)) {
        throw new SchedulingError('You have already booked this time slot');
      }

      const booking: Booking = {
        userId,
        userEmail,
        bookedAt: new Date().toISOString()
      };

      bookedBy.push(booking);

      const isAvailable = bookedBy.length < (slotData.capacity || 1);

      await updateDoc(slotRef, {
        bookedBy,
        isAvailable,
        updatedAt: serverTimestamp()
      });

      const lessonRef = doc(collection(db, 'users', userId, 'lessons'));

      await setDoc(lessonRef, {
        userId,
        slotId,
        date: slotData.date,
        startTime: slotData.startTime,
        endTime: slotData.endTime,
        location: slotData.location || 'TBD',
        instructor: slotData.instructor || 'TBD',
        status: 'scheduled',
        courseId: COURSE_IDS.BEHIND_WHEEL,
        ...getFirestoreTimestamps()
      });

      this.log(`User ${userId} booked time slot ${slotId}`);

      return {
        success: true,
        booking,
        slot: slotData
      };
    } catch (error) {
      this.logError(error, { method: 'bookTimeSlot', userId, slotId });
      throw error;
    }
  }

  async assignTimeSlot(slotId: string, userId: string): Promise<{ success: boolean; slotId: string; userId: string; slot: DocumentData }> {
    try {
      if (!slotId || typeof slotId !== 'string') {
        throw new ValidationError('Slot ID is required');
      }
      this.validate.validateUserId(userId);

      const slotRef = doc(db, 'timeSlots', slotId);
      const slotDoc = await getDoc(slotRef);

      if (!slotDoc.exists()) {
        throw new SchedulingError('Time slot not found');
      }

      const slotData = slotDoc.data();

      if (slotData.assignedTo && slotData.assignedTo === userId) {
        throw new SchedulingError('This slot is already assigned to this user');
      }

      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) {
        throw new ValidationError('User not found');
      }
      const userData = userDoc.data();

      const lessonRef = doc(collection(db, 'users', userId, 'lessons'));

      await setDoc(lessonRef, {
        userId,
        slotId,
        date: slotData.date,
        startTime: slotData.startTime,
        endTime: slotData.endTime,
        location: slotData.location || 'TBD',
        instructor: slotData.instructor || 'TBD',
        status: 'assigned',
        courseId: COURSE_IDS.BEHIND_WHEEL,
        assignedAt: new Date().toISOString(),
        ...getFirestoreTimestamps()
      });

      await updateDoc(slotRef, {
        assignedTo: userId,
        assignedToEmail: userData.email,
        assignedAt: serverTimestamp(),
        isAvailable: false,
        updatedAt: serverTimestamp()
      });

      this.log(`Admin assigned slot ${slotId} to user ${userId}`);

      return {
        success: true,
        slotId,
        userId,
        slot: slotData
      };
    } catch (error) {
      this.logError(error, { method: 'assignTimeSlot', slotId, userId });
      throw error;
    }
  }

  async unassignTimeSlot(slotId: string, userId: string): Promise<{ success: boolean }> {
    try {
      if (!slotId || typeof slotId !== 'string') {
        throw new ValidationError('Slot ID is required');
      }
      this.validate.validateUserId(userId);

      const slotRef = doc(db, 'timeSlots', slotId);
      const slotDoc = await getDoc(slotRef);

      if (!slotDoc.exists()) {
        throw new SchedulingError('Time slot not found');
      }

      const slotData = slotDoc.data();
      if (slotData.assignedTo !== userId) {
        throw new SchedulingError('This slot is not assigned to this user');
      }

      const lessonsRef = collection(db, 'users', userId, 'lessons');
      const q = query(lessonsRef, where('slotId', '==', slotId));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const lessonDoc = querySnapshot.docs[0];
        await deleteDoc(lessonDoc.ref);
      }

      await updateDoc(slotRef, {
        assignedTo: null,
        assignedToEmail: null,
        assignedAt: null,
        isAvailable: true,
        updatedAt: serverTimestamp()
      });

      this.log(`Admin unassigned slot ${slotId} from user ${userId}`);

      return { success: true };
    } catch (error) {
      this.logError(error, { method: 'unassignTimeSlot', slotId, userId });
      throw error;
    }
  }

  async getSlotsByAssignment(userId: string): Promise<TimeSlot[]> {
    try {
      this.validate.validateUserId(userId);

      const slotsRef = collection(db, 'timeSlots');
      const q = query(slotsRef, where('assignedTo', '==', userId));
      const querySnapshot = await getDocs(q);

      const slots: TimeSlot[] = [];
      querySnapshot.forEach(docSnap => {
        slots.push({
          id: docSnap.id,
          ...docSnap.data()
        } as TimeSlot);
      });

      return slots.sort((a, b) => {
        const timeA = new Date(`${a.date} ${a.startTime}`).getTime();
        const timeB = new Date(`${b.date} ${b.startTime}`).getTime();
        return timeA - timeB;
      });
    } catch (error) {
      this.logError(error, { method: 'getSlotsByAssignment', userId });
      throw error;
    }
  }

  async getUserBookings(userId: string): Promise<DocumentData[]> {
    try {
      this.validate.validateUserId(userId);

      const lessonsRef = collection(db, 'users', userId, 'lessons');
      const querySnapshot = await getDocs(lessonsRef);

      const bookings: DocumentData[] = [];
      querySnapshot.forEach(docSnap => {
        bookings.push({
          id: docSnap.id,
          ...docSnap.data()
        });
      });

      return bookings.sort((a, b) => {
        const timeA = new Date(`${a.date} ${a.startTime}`).getTime();
        const timeB = new Date(`${b.date} ${b.startTime}`).getTime();
        return timeA - timeB;
      });
    } catch (error) {
      this.logError(error, { method: 'getUserBookings', userId });
      throw error;
    }
  }

  async cancelBooking(userId: string, lessonId: string, slotId: string): Promise<{ success: boolean }> {
    try {
      this.validate.validateUserId(userId);
      if (!lessonId || typeof lessonId !== 'string') {
        throw new ValidationError('Lesson ID is required');
      }
      if (!slotId || typeof slotId !== 'string') {
        throw new ValidationError('Slot ID is required');
      }

      const lessonRef = doc(db, 'users', userId, 'lessons', lessonId);
      await deleteDoc(lessonRef);

      const slotRef = doc(db, 'timeSlots', slotId);
      const slotDoc = await getDoc(slotRef);

      if (slotDoc.exists()) {
        const slotData = slotDoc.data();
        const bookedBy = (slotData.bookedBy || []).filter((b: Booking) => b.userId !== userId);

        await updateDoc(slotRef, {
          bookedBy,
          isAvailable: true,
          updatedAt: serverTimestamp()
        });
      }

      this.log(`User ${userId} cancelled booking ${lessonId} for slot ${slotId}`);

      return { success: true };
    } catch (error) {
      this.logError(error, { method: 'cancelBooking', userId, lessonId, slotId });
      throw error;
    }
  }

  async updateTimeSlot(slotId: string, updates: Partial<TimeSlotData>): Promise<TimeSlot> {
    try {
      if (!slotId || typeof slotId !== 'string') {
        throw new ValidationError('Slot ID is required');
      }
      if (typeof updates !== 'object' || !updates) {
        throw new ValidationError('Updates must be a valid object');
      }

      const slotRef = doc(db, 'timeSlots', slotId);

      await updateDoc(slotRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });

      const slotDoc = await getDoc(slotRef);
      this.log(`Admin updated time slot ${slotId}`);

      return {
        id: slotDoc.id,
        ...(slotDoc.data() as DocumentData)
      } as TimeSlot;
    } catch (error) {
      this.logError(error, { method: 'updateTimeSlot', slotId });
      throw error;
    }
  }

  async deleteTimeSlot(slotId: string): Promise<{ success: boolean }> {
    try {
      if (!slotId || typeof slotId !== 'string') {
        throw new ValidationError('Slot ID is required');
      }

      const slotRef = doc(db, 'timeSlots', slotId);
      await deleteDoc(slotRef);
      this.log(`Admin deleted time slot ${slotId}`);
      return { success: true };
    } catch (error) {
      this.logError(error, { method: 'deleteTimeSlot', slotId });
      throw error;
    }
  }
}

const schedulingServices = new SchedulingService();
export default schedulingServices;

// Named exports for backward compatibility
export const createTimeSlot = (timeSlotData: TimeSlotData) => schedulingServices.createTimeSlot(timeSlotData);
export const getTimeSlots = (filters?: FilterOptions) => schedulingServices.getTimeSlots(filters);
export const getAvailableTimeSlots = (startDate: Date, endDate: Date) => schedulingServices.getAvailableTimeSlots(startDate, endDate);
export const bookTimeSlot = (userId: string, slotId: string, userEmail: string) => schedulingServices.bookTimeSlot(userId, slotId, userEmail);
export const assignTimeSlot = (slotId: string, userId: string) => schedulingServices.assignTimeSlot(slotId, userId);
export const unassignTimeSlot = (slotId: string, userId: string) => schedulingServices.unassignTimeSlot(slotId, userId);
export const getSlotsByAssignment = (userId: string) => schedulingServices.getSlotsByAssignment(userId);
export const getUserBookings = (userId: string) => schedulingServices.getUserBookings(userId);
export const cancelBooking = (userId: string, lessonId: string, slotId: string) => schedulingServices.cancelBooking(userId, lessonId, slotId);
export const updateTimeSlot = (slotId: string, updates: Partial<TimeSlotData>) => schedulingServices.updateTimeSlot(slotId, updates);
export const deleteTimeSlot = (slotId: string) => schedulingServices.deleteTimeSlot(slotId);
